import { useContext } from 'react';

import { useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { CollectionRequestParams } from './useCollection';
import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import LoginContext from '../../../aurr/contexts/LoginContext';
import type { Collection } from '../../../common/type';
import { COLLECTION_MUTATE_KEY, COLLECTION_QUERY_KEY } from '../constants';

export const useCollectionSave = (
  params: CollectionRequestParams & { [p: string]: string }
) => {
  const { COMMUNITY_FUNCTIONS_URL } = useContext(ConfigContext);
  const { baseURL, id, pathname, ...restParams } = params;
  const { access_token } = useContext(LoginContext);

  const parsedBaseURL = baseURL || COMMUNITY_FUNCTIONS_URL;

  const axiosConfig = {
    baseURL: `${parsedBaseURL}/store/v1/collections/${id}`,
    headers: {
      ...(access_token && { authorization: `Bearer ${access_token}` }),
    },
  };
  const api = axios.create(axiosConfig);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [COLLECTION_MUTATE_KEY, parsedBaseURL, id],
    mutationFn: async (payload: Partial<Collection>) => {
      const response = await api.patch(
        '',
        payload,
        restParams ? { params: restParams } : {}
      );
      // eslint-disable-next-line no-console

      return response?.data;
    },
    onMutate: async payload => {
      // Snapshot the previous value
      const savedCollections = queryClient.getQueryData([
        COLLECTION_QUERY_KEY,
        parsedBaseURL,
        id,
      ]);

      queryClient.setQueryData<Collection | undefined>(
        [COLLECTION_QUERY_KEY, parsedBaseURL, id],
        old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            isOptimistic: true,
            ...payload,
          };
        }
      );
      return { savedCollections };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, payload, context) => {
      queryClient.setQueryData(
        [COLLECTION_QUERY_KEY, parsedBaseURL, id],
        context?.savedCollections
      );
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEY, parsedBaseURL, id],
      });
    },
  });
};
